import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useRefData } from "@/context/RefDataContext";
import { PageHeader, StatusBadge, fmtDKK, fmtDate } from "@/components/shared";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Progress as ProgressBar } from "@/components/ui/progress";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Wallet, TrendingUp, AlertCircle, Ticket, BadgeCheck, Package, Building2, Coins,
  Plus, Receipt, CreditCard, Percent,
} from "lucide-react";
import { toast } from "sonner";

const PRODUCT_TYPES = ["single_class", "course_package", "private_session", "membership", "clip_card", "franchise_fee", "royalty_fee"];
const PAYMENT_STATUSES = ["unpaid", "pending", "paid", "failed", "refunded", "cancelled"];
const BILLING = ["monthly", "quarterly", "yearly"];

export default function Payments() {
  const { user } = useAuth();
  const ref = useRefData();
  const role = user?.role;
  const isGlobal = ["super_admin", "head_office_admin"].includes(role);
  const canManage = ["super_admin", "head_office_admin", "franchise_owner", "location_manager"].includes(role);
  const [dash, setDash] = useState(null);
  const [payments, setPayments] = useState([]);
  const [products, setProducts] = useState([]);
  const [memberships, setMemberships] = useState([]);
  const [packages, setPackages] = useState([]);
  const [royalty, setRoyalty] = useState([]);
  const [fees, setFees] = useState([]);
  const [reports, setReports] = useState(null);
  const [err, setErr] = useState(false);
  const [loadErr, setLoadErr] = useState(false);

  const load = useCallback(async () => {
    try {
      const d = await api.get("/payments/dashboard"); setDash(d.data);
      if (d.data.scope !== "client") {
        const [p, pr, m, pk, ro, fe, rp] = await Promise.all([
          api.get("/payments"), api.get("/products"), api.get("/memberships"), api.get("/packages"),
          api.get("/royalty"), api.get("/franchise-fees"), api.get("/payments/reports"),
        ]);
        setPayments(p.data); setProducts(pr.data); setMemberships(m.data); setPackages(pk.data);
        setRoyalty(ro.data); setFees(fe.data); setReports(rp.data);
      }
    } catch (e) { if (e?.response?.status === 403) setErr(true); else setLoadErr(true); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (err) return <div className="text-center py-20 text-muted-foreground" data-testid="payments-no-access">Financial dashboards are not available for your role.</div>;
  if (loadErr) return <div className="text-center py-20 text-muted-foreground" data-testid="payments-load-error">Couldn't load HV Payments. Please refresh to try again.</div>;
  if (!dash) return <div className="text-center py-20 text-muted-foreground">Loading...</div>;

  if (dash.scope === "client") return <ClientPayments dash={dash} />;

  return (
    <div>
      <PageHeader title="HV Payments" subtitle={`Revenue, memberships, packages, royalty and franchise fees${dash.scope === "network" ? " across the network" : " for your location"}. Placeholder billing — no live payment processing yet.`} />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <Stat icon={Wallet} n={fmtDKK(dash.total_revenue)} label="Total Revenue" tone="text-emerald-700" />
        <Stat icon={TrendingUp} n={fmtDKK(dash.revenue_this_month)} label="Revenue This Month" />
        <Stat icon={AlertCircle} n={fmtDKK(dash.outstanding_payments)} label="Outstanding" tone="text-amber-700" sub={`${dash.outstanding_count} payments`} />
        <Stat icon={Ticket} n={dash.paid_bookings} label="Paid Bookings" sub={`${dash.unpaid_bookings} unpaid`} />
        <Stat icon={BadgeCheck} n={dash.active_memberships} label="Active Memberships" />
        <Stat icon={Package} n={dash.package_sales} label="Packages" />
        <Stat icon={Building2} n={fmtDKK(dash.franchise_fees_due)} label="Franchise Fees Due" tone="text-amber-700" />
        <Stat icon={Coins} n={fmtDKK(dash.royalty_due)} label="Royalty Due" tone="text-orange-700" sub={`${fmtDKK(dash.royalty_paid)} paid`} />
      </div>

      <Tabs defaultValue="overview">
        <TabsList className="h-auto flex-wrap">
          <TabsTrigger value="overview" data-testid="pay-tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="payments" data-testid="pay-tab-payments">Payments</TabsTrigger>
          <TabsTrigger value="products" data-testid="pay-tab-products">Products</TabsTrigger>
          <TabsTrigger value="memberships" data-testid="pay-tab-memberships">Memberships</TabsTrigger>
          <TabsTrigger value="packages" data-testid="pay-tab-packages">Packages</TabsTrigger>
          <TabsTrigger value="royalty" data-testid="pay-tab-royalty">Royalty</TabsTrigger>
          <TabsTrigger value="fees" data-testid="pay-tab-fees">Franchise Fees</TabsTrigger>
          <TabsTrigger value="reports" data-testid="pay-tab-reports">Reports</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-4"><Overview dash={dash} /></TabsContent>
        <TabsContent value="payments" className="mt-4"><PaymentsTab payments={payments} canManage={canManage} refData={ref} reload={load} /></TabsContent>
        <TabsContent value="products" className="mt-4"><ProductsTab products={products} canManage={canManage} isGlobal={isGlobal} refData={ref} reload={load} /></TabsContent>
        <TabsContent value="memberships" className="mt-4"><MembershipsTab memberships={memberships} canManage={canManage} refData={ref} reload={load} /></TabsContent>
        <TabsContent value="packages" className="mt-4"><PackagesTab packages={packages} canManage={canManage} refData={ref} reload={load} /></TabsContent>
        <TabsContent value="royalty" className="mt-4"><RoyaltyTab royalty={royalty} isGlobal={isGlobal} refData={ref} reload={load} /></TabsContent>
        <TabsContent value="fees" className="mt-4"><FeesTab fees={fees} isGlobal={isGlobal} refData={ref} reload={load} /></TabsContent>
        <TabsContent value="reports" className="mt-4"><ReportsTab reports={reports} /></TabsContent>
      </Tabs>
    </div>
  );
}

/* ---------- Overview ---------- */
function Overview({ dash }) {
  const rev = dash.revenue_by_location || dash.revenue_by_program || [];
  const label = dash.scope === "network" ? "Revenue by Location" : "Revenue by Program";
  const max = Math.max(1, ...rev.map((r) => r.value));
  return (
    <div className="grid lg:grid-cols-2 gap-4">
      <Card className="border-border"><CardHeader><CardTitle className="font-heading text-base flex items-center gap-2"><TrendingUp size={16} /> {label}</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {rev.map((r) => (
            <div key={r.name} data-testid="overview-rev-row"><div className="flex justify-between text-sm mb-1"><span>{r.name}</span><span className="font-medium">{fmtDKK(r.value)}</span></div><ProgressBar value={(r.value / max) * 100} className="h-2" /></div>
          ))}
          {rev.length === 0 && <p className="text-sm text-muted-foreground py-4 text-center">No paid revenue yet.</p>}
        </CardContent>
      </Card>
      <Card className="border-border"><CardHeader><CardTitle className="font-heading text-base flex items-center gap-2"><Coins size={16} /> Royalty & Fees</CardTitle></CardHeader>
        <CardContent className="space-y-2 text-sm">
          <Row label="Royalty due" value={fmtDKK(dash.royalty_due)} tone="text-orange-700" />
          <Row label="Royalty paid" value={fmtDKK(dash.royalty_paid)} tone="text-green-700" />
          <Row label="Overdue royalty" value={fmtDKK(dash.overdue_royalty)} tone="text-red-700" />
          <Row label="Franchise fees due" value={fmtDKK(dash.franchise_fees_due)} tone="text-amber-700" />
          <Row label="Refunds" value={fmtDKK(dash.refunds)} />
        </CardContent>
      </Card>
    </div>
  );
}
const Row = ({ label, value, tone }) => (<div className="flex justify-between border-b border-border/40 pb-2"><span className="text-muted-foreground">{label}</span><span className={`font-medium ${tone || ""}`}>{value}</span></div>);

/* ---------- Payments ---------- */
function PaymentsTab({ payments, canManage, refData, reload }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ client_id: "", location_id: "", amount: "", payment_status: "unpaid", description: "" });
  const setStatus = async (p, status) => { try { await api.patch(`/payments/${p.id}`, { payment_status: status }); toast.success("Payment updated"); reload(); } catch { toast.error("Failed"); } };
  const create = async () => { try { await api.post("/payments", { ...form, amount: Number(form.amount) || 0 }); toast.success("Payment created"); setOpen(false); reload(); } catch { toast.error("Failed"); } };
  return (
    <div>
      {canManage && <div className="flex justify-end mb-3"><Button size="sm" className="gap-2" onClick={() => setOpen(true)} data-testid="payment-add-btn"><Plus size={15} /> New Payment</Button></div>}
      <TableCard head={["Client", "Class / Item", "Location", "Amount", "Status", canManage ? "Set status" : ""]}>
        {payments.map((p) => (
          <tr key={p.id} className="border-b border-border/50" data-testid="payment-row">
            <td className="px-4 py-3 font-medium">{p.client_name || "—"}</td>
            <td className="px-4 py-3 text-muted-foreground">{p.class_name || p.product_name || p.description || "—"}</td>
            <td className="px-4 py-3 hidden sm:table-cell text-muted-foreground">{p.location_name || "—"}</td>
            <td className="px-4 py-3 font-medium">{fmtDKK(p.amount)}</td>
            <td className="px-4 py-3"><StatusBadge value={p.payment_status} /></td>
            {canManage && <td className="px-4 py-3">
              <Select value={p.payment_status} onValueChange={(v) => setStatus(p, v)}><SelectTrigger className="h-8 w-32 capitalize" data-testid="payment-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>{PAYMENT_STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent></Select>
            </td>}
          </tr>
        ))}
        {payments.length === 0 && <EmptyRow cols={6} text="No payments yet." />}
      </TableCard>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">New Payment</DialogTitle><DialogDescription>Record a manual payment (placeholder).</DialogDescription></DialogHeader>
          <div className="grid gap-4 py-2">
            <Sel label="Client" value={form.client_id} onChange={(v) => setForm({ ...form, client_id: v })} options={(refData?.clients || []).map((c) => ({ v: c.id, l: c.full_name }))} testid="payment-client" />
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-1.5"><Label>Amount (DKK)</Label><Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="payment-amount" /></div>
              <Sel label="Status" value={form.payment_status} onChange={(v) => setForm({ ...form, payment_status: v })} options={PAYMENT_STATUSES.map((s) => ({ v: s, l: s }))} />
            </div>
            <div className="grid gap-1.5"><Label>Description</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={create} disabled={!form.client_id || !form.amount} data-testid="payment-save">Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------- Products ---------- */
function ProductsTab({ products, canManage, isGlobal, refData, reload }) {
  const [dlg, setDlg] = useState(null);
  const empty = { name: "", product_type: "single_class", price: "", description: "", is_global: isGlobal, is_active: true, training_program_id: "" };
  const [form, setForm] = useState(empty);
  const open = (p) => { setForm(p ? { name: p.name, product_type: p.product_type, price: p.price, description: p.description, is_global: p.is_global, is_active: p.is_active, training_program_id: p.training_program_id || "" } : empty); setDlg({ editing: p }); };
  const save = async () => {
    try {
      const payload = { ...form, price: Number(form.price) || 0, training_program_id: form.training_program_id || null };
      if (dlg.editing) await api.put(`/products/${dlg.editing.id}`, payload); else await api.post("/products", payload);
      toast.success("Product saved"); setDlg(null); reload();
    } catch { toast.error("Failed"); }
  };
  const del = async (p) => { if (!window.confirm(`Delete "${p.name}"?`)) return; await api.delete(`/products/${p.id}`); toast.success("Deleted"); reload(); };
  return (
    <div>
      {canManage && <div className="flex justify-end mb-3"><Button size="sm" className="gap-2" onClick={() => open(null)} data-testid="product-add-btn"><Plus size={15} /> New Product</Button></div>}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {products.map((p) => (
          <Card key={p.id} className="border-border" data-testid="product-card"><CardContent className="p-4">
            <div className="flex items-start justify-between">
              <div><div className="font-heading font-semibold">{p.name}</div><div className="text-xs text-muted-foreground capitalize">{String(p.product_type).replace(/_/g, " ")} · {p.is_global ? "Global" : p.location_name}</div></div>
              <StatusBadge value={p.is_active ? "active" : "inactive"} />
            </div>
            <div className="text-2xl font-heading font-semibold mt-2">{fmtDKK(p.price)}</div>
            {p.description && <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{p.description}</p>}
            {canManage && <div className="flex gap-2 mt-3"><Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => open(p)} data-testid="product-edit-btn">Edit</Button><Button size="sm" variant="ghost" className="h-7 text-xs text-destructive" onClick={() => del(p)}>Delete</Button></div>}
          </CardContent></Card>
        ))}
        {products.length === 0 && <p className="text-muted-foreground col-span-full text-center py-10">No products yet.</p>}
      </div>

      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">{dlg?.editing ? "Edit Product" : "New Product"}</DialogTitle><DialogDescription>Products & offers sold at Hundeverket.</DialogDescription></DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="grid gap-1.5"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="product-name" /></div>
            <div className="grid grid-cols-2 gap-4">
              <Sel label="Type" value={form.product_type} onChange={(v) => setForm({ ...form, product_type: v })} options={PRODUCT_TYPES.map((t) => ({ v: t, l: t }))} testid="product-type" />
              <div className="grid gap-1.5"><Label>Price (DKK)</Label><Input type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="product-price" /></div>
            </div>
            <Sel label="Linked program (optional)" value={form.training_program_id || "none"} onChange={(v) => setForm({ ...form, training_program_id: v === "none" ? "" : v })} options={[{ v: "none", l: "None" }, ...(refData?.programs || []).map((p) => ({ v: p.id, l: p.name }))]} />
            <div className="grid gap-1.5"><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <div className="flex gap-4">
              {isGlobal && <div className="flex items-center gap-2 rounded-lg border border-border/60 p-3 flex-1"><Label className="flex-1">Global product</Label><Switch checked={!!form.is_global} onCheckedChange={(v) => setForm({ ...form, is_global: v })} /></div>}
              <div className="flex items-center gap-2 rounded-lg border border-border/60 p-3 flex-1"><Label className="flex-1">Active</Label><Switch checked={!!form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} /></div>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setDlg(null)}>Cancel</Button><Button onClick={save} disabled={!form.name} data-testid="product-save">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------- Memberships ---------- */
function MembershipsTab({ memberships, canManage, refData, reload }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ client_id: "", dog_id: "", membership_type: "Monthly Training Membership", price: "", billing_cycle: "monthly", status: "active", payment_status: "paid" });
  const create = async () => { try { await api.post("/memberships", { ...form, price: Number(form.price) || 0, dog_id: form.dog_id || null }); toast.success("Membership created"); setOpen(false); reload(); } catch { toast.error("Failed"); } };
  const setStatus = async (m, status) => { try { await api.patch(`/memberships/${m.id}`, { status }); toast.success("Updated"); reload(); } catch { toast.error("Failed"); } };
  return (
    <div>
      {canManage && <div className="flex justify-end mb-3"><Button size="sm" className="gap-2" onClick={() => setOpen(true)} data-testid="membership-add-btn"><Plus size={15} /> New Membership</Button></div>}
      <TableCard head={["Client", "Type", "Cycle", "Price", "Payment", "Status", canManage ? "Set" : ""]}>
        {memberships.map((m) => (
          <tr key={m.id} className="border-b border-border/50" data-testid="membership-row">
            <td className="px-4 py-3 font-medium">{m.client_name}</td>
            <td className="px-4 py-3 text-muted-foreground">{m.membership_type}</td>
            <td className="px-4 py-3 capitalize text-muted-foreground">{m.billing_cycle}</td>
            <td className="px-4 py-3">{fmtDKK(m.price)}</td>
            <td className="px-4 py-3"><StatusBadge value={m.payment_status} /></td>
            <td className="px-4 py-3"><StatusBadge value={m.status} /></td>
            {canManage && <td className="px-4 py-3">
              <Select value={m.status} onValueChange={(v) => setStatus(m, v)}><SelectTrigger className="h-8 w-28 capitalize"><SelectValue /></SelectTrigger>
                <SelectContent>{["active", "paused", "cancelled", "expired"].map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent></Select>
            </td>}
          </tr>
        ))}
        {memberships.length === 0 && <EmptyRow cols={7} text="No memberships yet." />}
      </TableCard>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">New Membership</DialogTitle><DialogDescription>Placeholder membership.</DialogDescription></DialogHeader>
          <div className="grid gap-4 py-2">
            <Sel label="Client" value={form.client_id} onChange={(v) => setForm({ ...form, client_id: v })} options={(refData?.clients || []).map((c) => ({ v: c.id, l: c.full_name }))} testid="membership-client" />
            <div className="grid gap-1.5"><Label>Membership type</Label><Input value={form.membership_type} onChange={(e) => setForm({ ...form, membership_type: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-1.5"><Label>Price (DKK)</Label><Input type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="membership-price" /></div>
              <Sel label="Billing" value={form.billing_cycle} onChange={(v) => setForm({ ...form, billing_cycle: v })} options={BILLING.map((b) => ({ v: b, l: b }))} />
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={create} disabled={!form.client_id} data-testid="membership-save">Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------- Packages ---------- */
function PackagesTab({ packages, canManage, refData, reload }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ client_id: "", dog_id: "", package_name: "10-Class Clip Card", included_sessions: 10, used_sessions: 0, status: "active" });
  const create = async () => { try { await api.post("/packages", { ...form, included_sessions: Number(form.included_sessions), dog_id: form.dog_id || null }); toast.success("Package created"); setOpen(false); reload(); } catch { toast.error("Failed"); } };
  const spendSession = async (p) => { try { await api.patch(`/packages/${p.id}`, { used_sessions: Math.min(p.included_sessions, (p.used_sessions || 0) + 1) }); toast.success("Session used"); reload(); } catch { toast.error("Failed"); } };
  return (
    <div>
      {canManage && <div className="flex justify-end mb-3"><Button size="sm" className="gap-2" onClick={() => setOpen(true)} data-testid="package-add-btn"><Plus size={15} /> New Package</Button></div>}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {packages.map((p) => (
          <Card key={p.id} className="border-border" data-testid="package-card"><CardContent className="p-4">
            <div className="flex items-start justify-between"><div><div className="font-heading font-semibold">{p.package_name}</div><div className="text-xs text-muted-foreground">{p.client_name}{p.dog_name ? ` · ${p.dog_name}` : ""}</div></div><StatusBadge value={p.status} /></div>
            <div className="mt-3"><div className="flex justify-between text-xs text-muted-foreground mb-1"><span>Used {p.used_sessions}/{p.included_sessions}</span><span>{p.remaining_sessions} left</span></div><ProgressBar value={(p.used_sessions / (p.included_sessions || 1)) * 100} className="h-2" /></div>
            {p.expiry_date && <div className="text-xs text-muted-foreground mt-2">Expires {fmtDate(p.expiry_date)}</div>}
            {canManage && <Button size="sm" variant="outline" className="h-7 text-xs mt-3" onClick={() => spendSession(p)} data-testid="package-use-btn" disabled={p.remaining_sessions <= 0}>Use a session</Button>}
          </CardContent></Card>
        ))}
        {packages.length === 0 && <p className="text-muted-foreground col-span-full text-center py-10">No packages yet.</p>}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">New Package / Clip Card</DialogTitle><DialogDescription>Placeholder clip card.</DialogDescription></DialogHeader>
          <div className="grid gap-4 py-2">
            <Sel label="Client" value={form.client_id} onChange={(v) => setForm({ ...form, client_id: v })} options={(refData?.clients || []).map((c) => ({ v: c.id, l: c.full_name }))} testid="package-client" />
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-1.5"><Label>Package name</Label><Input value={form.package_name} onChange={(e) => setForm({ ...form, package_name: e.target.value })} /></div>
              <div className="grid gap-1.5"><Label>Included sessions</Label><Input type="number" value={form.included_sessions} onChange={(e) => setForm({ ...form, included_sessions: e.target.value })} data-testid="package-sessions" /></div>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={create} disabled={!form.client_id} data-testid="package-save">Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------- Royalty ---------- */
function RoyaltyTab({ royalty, isGlobal, refData, reload }) {
  const [open, setOpen] = useState(false);
  const today = new Date();
  const [form, setForm] = useState({ location_id: "", period_start: new Date(today.getFullYear(), today.getMonth(), 1).toISOString().slice(0, 10), period_end: today.toISOString().slice(0, 10), royalty_rate: 7 });
  const generate = async () => { try { await api.post("/royalty/generate", { ...form, royalty_rate: Number(form.royalty_rate) }); toast.success("Royalty report generated"); setOpen(false); reload(); } catch { toast.error("Failed"); } };
  const markPaid = async (r) => { try { await api.patch(`/royalty/${r.id}`, { status: "paid" }); toast.success("Marked paid"); reload(); } catch { toast.error("Failed"); } };
  return (
    <div>
      {isGlobal && <div className="flex justify-end mb-3"><Button size="sm" className="gap-2" onClick={() => setOpen(true)} data-testid="royalty-generate-btn"><Percent size={15} /> Generate Report</Button></div>}
      <TableCard head={["Location", "Period", "Gross Revenue", "Rate", "Royalty Due", "Status", isGlobal ? "" : ""]}>
        {royalty.map((r) => (
          <tr key={r.id} className="border-b border-border/50" data-testid="royalty-row">
            <td className="px-4 py-3 font-medium">{r.location_name}</td>
            <td className="px-4 py-3 text-muted-foreground text-xs">{fmtDate(r.period_start)} – {fmtDate(r.period_end)}</td>
            <td className="px-4 py-3">{fmtDKK(r.gross_revenue)}</td>
            <td className="px-4 py-3">{r.royalty_rate}%</td>
            <td className="px-4 py-3 font-medium">{fmtDKK(r.royalty_due)}</td>
            <td className="px-4 py-3"><StatusBadge value={r.status} /></td>
            <td className="px-4 py-3">{isGlobal && r.status !== "paid" && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => markPaid(r)} data-testid="royalty-mark-paid">Mark paid</Button>}</td>
          </tr>
        ))}
        {royalty.length === 0 && <EmptyRow cols={7} text="No royalty reports yet." />}
      </TableCard>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">Generate Royalty Report</DialogTitle><DialogDescription>Royalty is calculated from paid revenue in the period (placeholder).</DialogDescription></DialogHeader>
          <div className="grid gap-4 py-2">
            <Sel label="Location" value={form.location_id} onChange={(v) => setForm({ ...form, location_id: v })} options={(refData?.locations || []).map((l) => ({ v: l.id, l: l.name }))} testid="royalty-location" />
            <div className="grid grid-cols-3 gap-4">
              <div className="grid gap-1.5"><Label>From</Label><Input type="date" value={form.period_start} onChange={(e) => setForm({ ...form, period_start: e.target.value })} /></div>
              <div className="grid gap-1.5"><Label>To</Label><Input type="date" value={form.period_end} onChange={(e) => setForm({ ...form, period_end: e.target.value })} /></div>
              <div className="grid gap-1.5"><Label>Rate %</Label><Input type="number" value={form.royalty_rate} onChange={(e) => setForm({ ...form, royalty_rate: e.target.value })} data-testid="royalty-rate" /></div>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={generate} disabled={!form.location_id} data-testid="royalty-save">Generate</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------- Franchise Fees ---------- */
function FeesTab({ fees, isGlobal, refData, reload }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ location_id: "", fee_type: "monthly_fee", amount: 3000, due_date: "", notes: "" });
  const create = async () => { try { await api.post("/franchise-fees", { ...form, amount: Number(form.amount) }); toast.success("Fee created"); setOpen(false); reload(); } catch { toast.error("Failed"); } };
  const setStatus = async (f, status) => { try { await api.patch(`/franchise-fees/${f.id}`, { status }); toast.success("Updated"); reload(); } catch { toast.error("Failed"); } };
  return (
    <div>
      {isGlobal && <div className="flex justify-end mb-3"><Button size="sm" className="gap-2" onClick={() => setOpen(true)} data-testid="fee-add-btn"><Plus size={15} /> New Fee</Button></div>}
      <TableCard head={["Location", "Fee Type", "Amount", "Due", "Status", isGlobal ? "Set" : ""]}>
        {fees.map((f) => (
          <tr key={f.id} className="border-b border-border/50" data-testid="fee-row">
            <td className="px-4 py-3 font-medium">{f.location_name}</td>
            <td className="px-4 py-3 capitalize text-muted-foreground">{String(f.fee_type).replace(/_/g, " ")}</td>
            <td className="px-4 py-3 font-medium">{fmtDKK(f.amount)}</td>
            <td className="px-4 py-3 text-muted-foreground text-xs">{fmtDate(f.due_date)}</td>
            <td className="px-4 py-3"><StatusBadge value={f.status} /></td>
            {isGlobal && <td className="px-4 py-3">
              <Select value={f.status} onValueChange={(v) => setStatus(f, v)}><SelectTrigger className="h-8 w-24 capitalize"><SelectValue /></SelectTrigger>
                <SelectContent>{["due", "paid", "overdue"].map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent></Select>
            </td>}
          </tr>
        ))}
        {fees.length === 0 && <EmptyRow cols={6} text="No franchise fees yet." />}
      </TableCard>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">New Franchise Fee</DialogTitle><DialogDescription>Placeholder franchise fee.</DialogDescription></DialogHeader>
          <div className="grid gap-4 py-2">
            <Sel label="Location" value={form.location_id} onChange={(v) => setForm({ ...form, location_id: v })} options={(refData?.locations || []).map((l) => ({ v: l.id, l: l.name }))} testid="fee-location" />
            <div className="grid grid-cols-2 gap-4">
              <Sel label="Fee type" value={form.fee_type} onChange={(v) => setForm({ ...form, fee_type: v })} options={[{ v: "startup_fee", l: "startup fee" }, { v: "monthly_fee", l: "monthly fee" }, { v: "annual_fee", l: "annual fee" }]} />
              <div className="grid gap-1.5"><Label>Amount (DKK)</Label><Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="fee-amount" /></div>
            </div>
            <div className="grid gap-1.5"><Label>Due date</Label><Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={create} disabled={!form.location_id} data-testid="fee-save">Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------- Reports ---------- */
function ReportsTab({ reports }) {
  if (!reports) return null;
  const pv = reports.paid_vs_unpaid || { paid: 0, unpaid: 0 };
  return (
    <div className="grid lg:grid-cols-2 gap-4">
      <MiniBars title="Revenue by Location" data={reports.revenue_by_location} testid="report-rev-loc" />
      <MiniBars title="Revenue by Program" data={reports.revenue_by_program} testid="report-rev-prog" />
      <Card className="border-border"><CardHeader><CardTitle className="font-heading text-base">Paid vs Unpaid Bookings</CardTitle></CardHeader>
        <CardContent className="flex gap-4"><div className="flex-1 text-center rounded-lg bg-green-50 py-4"><div className="text-2xl font-heading font-semibold text-green-700">{pv.paid}</div><div className="text-xs text-muted-foreground">Paid</div></div><div className="flex-1 text-center rounded-lg bg-amber-50 py-4"><div className="text-2xl font-heading font-semibold text-amber-700">{pv.unpaid}</div><div className="text-xs text-muted-foreground">Unpaid</div></div></CardContent>
      </Card>
      <Card className="border-border"><CardHeader><CardTitle className="font-heading text-base">Royalty Due by Location</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {reports.royalty_due_by_location.map((r) => (<div key={r.name} className="flex items-center justify-between text-sm"><span>{r.name}</span><span className="flex items-center gap-2">{fmtDKK(r.due)} <StatusBadge value={r.status} /></span></div>))}
          {reports.royalty_due_by_location.length === 0 && <p className="text-sm text-muted-foreground py-4 text-center">No royalty data.</p>}
        </CardContent>
      </Card>
    </div>
  );
}
function MiniBars({ title, data = [], testid }) {
  const max = Math.max(1, ...data.map((d) => d.value));
  return (
    <Card className="border-border"><CardHeader><CardTitle className="font-heading text-base">{title}</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        {data.map((d) => (<div key={d.name} data-testid={testid}><div className="flex justify-between text-sm mb-1"><span>{d.name}</span><span className="font-medium">{fmtDKK(d.value)}</span></div><ProgressBar value={(d.value / max) * 100} className="h-2" /></div>))}
        {data.length === 0 && <p className="text-sm text-muted-foreground py-4 text-center">No data.</p>}
      </CardContent>
    </Card>
  );
}

/* ---------- Client view ---------- */
function ClientPayments({ dash }) {
  return (
    <div>
      <PageHeader title="My Payments" subtitle="Your payment history, membership and packages." />
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
        <Stat icon={Wallet} n={fmtDKK(dash.total_paid)} label="Total Paid" tone="text-emerald-700" />
        <Stat icon={AlertCircle} n={fmtDKK(dash.outstanding)} label="Outstanding" tone="text-amber-700" />
        <Stat icon={BadgeCheck} n={dash.active_memberships} label="Active Memberships" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <h2 className="font-heading text-lg font-semibold mb-3">Payment History</h2>
          <TableCard head={["Class / Item", "Amount", "Status", "Receipt"]}>
            {dash.payments.map((p) => (
              <tr key={p.id} className="border-b border-border/50" data-testid="client-payment-row">
                <td className="px-4 py-3">{p.class_name || p.description || "—"}</td>
                <td className="px-4 py-3 font-medium">{fmtDKK(p.amount)}</td>
                <td className="px-4 py-3"><StatusBadge value={p.payment_status} /></td>
                <td className="px-4 py-3">{p.payment_status === "paid" ? <span className="text-xs text-primary flex items-center gap-1"><Receipt size={13} /> Receipt</span> : <span className="text-xs text-muted-foreground">—</span>}</td>
              </tr>
            ))}
            {dash.payments.length === 0 && <EmptyRow cols={4} text="No payments yet." />}
          </TableCard>
        </div>
        <div className="space-y-6">
          <Card className="border-border"><CardHeader><CardTitle className="font-heading text-base flex items-center gap-2"><BadgeCheck size={16} /> Membership</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {dash.memberships.map((m) => (<div key={m.id} className="rounded-lg border border-border/60 p-3" data-testid="client-membership"><div className="flex items-center justify-between"><span className="text-sm font-medium">{m.membership_type}</span><StatusBadge value={m.status} /></div><div className="text-xs text-muted-foreground mt-1">{fmtDKK(m.price)} / {m.billing_cycle} · {m.payment_status}</div></div>))}
              {dash.memberships.length === 0 && <p className="text-sm text-muted-foreground py-3 text-center">No active membership.</p>}
            </CardContent>
          </Card>
          <Card className="border-border"><CardHeader><CardTitle className="font-heading text-base flex items-center gap-2"><Package size={16} /> Clip Cards</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {dash.packages.map((p) => (<div key={p.id} className="rounded-lg border border-border/60 p-3" data-testid="client-package"><div className="flex items-center justify-between"><span className="text-sm font-medium">{p.package_name}</span><StatusBadge value={p.status} /></div><div className="mt-2"><div className="flex justify-between text-xs text-muted-foreground mb-1"><span>{p.used_sessions}/{p.included_sessions} used</span><span>{p.remaining_sessions} left</span></div><ProgressBar value={(p.used_sessions / (p.included_sessions || 1)) * 100} className="h-2" /></div></div>))}
              {dash.packages.length === 0 && <p className="text-sm text-muted-foreground py-3 text-center">No clip cards.</p>}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

/* ---------- shared bits ---------- */
const Stat = ({ icon: Icon, n, label, sub, tone }) => (
  <Card className="border-border"><CardContent className="p-4">
    <div className="flex items-center gap-2 text-muted-foreground mb-1.5"><Icon size={15} strokeWidth={1.5} /><span className="text-[11px] uppercase tracking-wide font-semibold">{label}</span></div>
    <div className={`text-xl font-heading font-semibold tracking-tight ${tone || ""}`}>{n}</div>
    {sub && <div className="text-[11px] text-muted-foreground mt-0.5">{sub}</div>}
  </CardContent></Card>
);

function TableCard({ head, children }) {
  return (
    <Card className="border-border"><CardContent className="p-0 overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="border-b border-border text-xs uppercase tracking-wide text-muted-foreground">
          <tr>{head.map((h) => <th key={h} className="text-left font-semibold px-4 py-3">{h}</th>)}</tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </CardContent></Card>
  );
}
const EmptyRow = ({ cols, text }) => (<tr><td colSpan={cols} className="text-center text-muted-foreground py-10">{text}</td></tr>);

function Sel({ label, value, onChange, options, testid }) {
  return (
    <div className="grid gap-1.5">
      <Label>{label}</Label>
      <Select value={value ?? ""} onValueChange={onChange}>
        <SelectTrigger data-testid={testid} className="capitalize"><SelectValue placeholder="Select" /></SelectTrigger>
        <SelectContent>{options.map((o) => <SelectItem key={o.v || "none"} value={o.v} className="capitalize">{String(o.l).replace(/_/g, " ")}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}
